import dayjs from "dayjs";
import _ from "lodash";
import { flow, types } from "mobx-state-tree";
import { getEnv } from "~/mobx/utils/getEnv";
import { getRoot } from "~/mobx/utils/getRoot";
import { delay } from "~/utils/delay";
import { Timeslot } from "./Timeslot";
import type { TimeslotInstance, TimeslotSnapshotIn } from "./Timeslot";
import type { AxiosResponse } from "axios";
import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";

export interface PetStoreInstance extends Instance<typeof TimeslotStore> {}
export interface PetStoreSnapshotIn extends SnapshotIn<typeof TimeslotStore> {}
export interface PetStoreSnapshotOut
  extends SnapshotOut<typeof TimeslotStore> {}

type PractitionersReturn = { [key: string]: string } & { areVisible?: boolean };

export const TimeslotStore = types
  .model("TimeslotStore", {
    map: types.map(Timeslot),
  })
  .actions((self) => {
    return {
      processOne(data: TimeslotSnapshotIn) {
        // We have to generate a unique id for each timeslot because the API does not provide one
        const id = `${data.startUtc}-${data.endUtc}-${data.practitioner.id}`;

        // Assign the generated id to the timeslot
        data.id = id;

        const existing = self.map.get(id);
        if (existing) {
          _.mergeWith(existing, data, (_, next) => {
            if (Array.isArray(next)) return next; // Treat arrays like atoms
          });
          return existing;
        }
        return self.map.put(data as TimeslotSnapshotIn);
      },
    };
  })
  .actions((self) => {
    return {
      processMany(dataList: TimeslotSnapshotIn[]) {
        return dataList.map((data) => {
          return self.processOne(data);
        });
      },
    };
  })
  .actions((self) => {
    return {
      getAvailability: flow<
        {
          availableDays: Set<string> | undefined;
          firstAvailableDay: string | undefined;
          practitioners: PractitionersReturn | undefined;
        },
        [string | null]
      >(function* getAvailability(practitionerId: string | null): any {
        const env = getEnv(self);
        const root = getRoot(self);

        const clinicId = root.bookingStore.clinicId;
        const reasonId = root.bookingStore.reasonId;

        // First we try to find available day within the next week
        const response: AxiosResponse = yield env.baseAuthHttp.get(
          `/clinics/${clinicId}/timeslots`,
          {
            params: {
              clinicId,
              reasonId,
              from: dayjs().tz("Etc/UTC").format("YYYY-MM-DDTHH:mm[Z]"),
              to: dayjs()
                .add(2, "month")
                .tz("Etc/UTC") // we have to convert to UTC because the API expects it
                .format("YYYY-MM-DDTHH:mm[Z]"),
            },
          },
        );

        const filterByPractitionerId = (timeslot: TimeslotInstance) =>
          practitionerId ? timeslot.practitioner.id === practitionerId : true;

        // Get the available days
        let availableDays = new Set();

        // filter results using the date provided practitionerId
        let data = practitionerId
          ? response.data.data.filter(filterByPractitionerId)
          : response.data.data;

        // Get the available practitioners during the period of time
        let practitioners: PractitionersReturn = {};
        for (const day of data) {
          availableDays.add(day.startUtc.slice(0, 10));

          if (day.practitioner.name) {
            practitioners[day.practitioner.name] = day.practitioner.id;
          }
        }

        const practitionersAreVisible = Object.keys(practitioners).length > 0;

        practitioners.areVisible = practitionersAreVisible;

        if (data.length > 0) {
          return {
            firstAvailableDay: data[0].startUtc,
            availableDays,
            practitioners,
          };
        }

        // if we cant find available day within the next week, we start searching month by month until we find one
        // up to 6 months

        let retryCount = 0;
        const maxRetryCount = 6;

        let from = dayjs();

        while (retryCount < maxRetryCount) {
          const to = from.add(1, "month");

          const response: AxiosResponse = yield env.baseAuthHttp.get(
            `/clinics/${clinicId}/timeslots`,
            {
              params: {
                clinicId,
                reasonId,
                from: from.tz("Etc/UTC").format("YYYY-MM-DDTHH:mm[Z]"),
                to: to
                  .tz("Etc/UTC") // we have to convert to UTC because the API expects it
                  .format("YYYY-MM-DDTHH:mm[Z]"),
              },
            },
          );

          // filter results using the date provided practitionerId
          data = practitionerId
            ? response.data.data.filter(filterByPractitionerId)
            : response.data.data;

          if (data.length > 0) {
            availableDays = new Set();
            practitioners = {};
            for (const day of data) {
              availableDays.add(day.startUtc.slice(0, 10));

              if (day.practitioner.name) {
                practitioners[day.practitioner.name] = day.practitioner.id;
              }
            }

            const practitionersAreVisible =
              Object.keys(practitioners).length > 0;

            practitioners.areVisible = practitionersAreVisible;

            return {
              firstAvailableDay: data[0].startUtc,
              availableDays,
              practitioners,
            };
          } else {
            yield delay(1000); // wait 1 second before trying again
            from = from.add(1, "month");
            retryCount++;
          }
        }

        return {
          firstAvailableDay: undefined,
          availableDays: undefined,
          practitioners: undefined,
        };
      }),

      getTimeslotList: flow<
        TimeslotInstance[],
        [
          {
            from: dayjs.Dayjs;
            to: dayjs.Dayjs;
          },
        ]
      >(function* getTimeslotList({
        from,
        to,
      }: {
        from: dayjs.Dayjs;
        to: dayjs.Dayjs;
      }): any {
        const env = getEnv(self);
        const root = getRoot(self);
        const clinicId = root.bookingStore.clinicId;
        const reasonId = root.bookingStore.reasonId;

        const response: AxiosResponse = yield env.baseAuthHttp.get(
          `/clinics/${clinicId}/timeslots`,
          {
            params: {
              clinicId,
              reasonId,
              from: from.tz("Etc/UTC").format("YYYY-MM-DDTHH:mm[Z]"),
              to: to
                .tz("Etc/UTC") // we have to convert to UTC because the API expects it
                .format("YYYY-MM-DDTHH:mm[Z]"),
            },
          },
        );

        const timeslots = response.data.data;

        response.data = self.processMany(timeslots);

        return response.data;
      }),
    };
  });
